import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { TFunction } from 'i18next'
import type { Constant } from '@pbt/pbt-ui-components'
import { LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import type { PreviousMedicalRecord } from '~/api/graphql/generated/types'
import {
  ConsentStatusName,
  PriorVetCareStatusName,
} from '~/constants/previousMedicalRecord'
import { getConsentStatus } from '~/store/reducers/constants'

// --- Consent texts hook ---

export interface ConsentTexts {
  consentText: string | null
  descriptionText: string
  showConsentLine: boolean
}

/**
 * Resolves consent display texts from consentStatus hashId using ConsentStatus constants from Redux.
 * Used by useConsentTexts and by tests (getConsentTexts).
 */
export function getConsentTexts(
  consentStatusId: string | undefined,
  t: TFunction,
  nullDescription: string,
  consentStatusConstants: Constant[],
): ConsentTexts {
  const consentEntity = Utils.findById(consentStatusId, consentStatusConstants)
  const showConsentLine = consentStatusId != null
  const consentText = consentEntity
    ? consentEntity?.name === ConsentStatusName.GRANTED
      ? t('Common:PMR_CONSENT.CONSENT_YES')
      : consentEntity?.name === ConsentStatusName.DENIED
        ? t('Common:PMR_CONSENT.CONSENT_NO')
        : LanguageUtils.getTranslatedFieldName(consentEntity)
    : null

  const descriptionText =
    consentEntity?.name === ConsentStatusName.GRANTED
      ? t('Common:PMR_CONSENT.CONSENT_YES_DESCRIPTION')
      : consentEntity?.name === ConsentStatusName.DENIED
        ? t('Common:PMR_CONSENT.CONSENT_NO_DESCRIPTION')
        : nullDescription

  return {
    consentText,
    descriptionText,
    showConsentLine,
  }
}

/**
 * Hook: resolves consent display texts from consentStatus hashId.
 * Uses useTranslation and useSelector(getConsentStatus) internally.
 */
export function useConsentTexts(
  consentStatusId: string | undefined,
  nullDescription: string,
): ConsentTexts {
  const { t } = useTranslation(['Common', 'TimeTable'])
  const consentStatusConstants = useSelector(getConsentStatus)
  return getConsentTexts(
    consentStatusId,
    t,
    nullDescription,
    consentStatusConstants,
  )
}

// --- Manual contact id (UUID v4) ---

/**
 * RFC 4122 UUID v4 pattern (e.g. crypto.randomUUID()).
 * Manual-added contacts use this id; we omit id when sending to API.
 */
export const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isManualContactId(id: string | undefined): boolean {
  return id != null && UUID_V4_REGEX.test(id)
}

// --- Simple message resolver ---

/**
 * Determines the simple message to display for previous medical records based on status.
 * Returns a translated message key or null if the full consent/clinics view should be shown.
 *
 * @param t - Translation function (handles namespace prefixing based on how it was created)
 * @param priorVetCareStatus - Prior vet care status name (YES, NO, UNKNOWN)
 * @param consentStatus - Consent status name (GRANTED, DENIED, DEFERRED)
 * @param previousMedicalRecord - The previous medical record object (used to check if undefined)
 * @param options - Optional configuration for translation key prefixes and DEFERRED message key
 * @returns Translated message string or null
 */
export function getSimplePmrMessage(
  t: TFunction,
  priorVetCareStatus: string | undefined,
  consentStatus: string | undefined,
  previousMedicalRecord: PreviousMedicalRecord | null | undefined,
  options?: {
    /** Translation key for UNKNOWN + DEFERRED case. If it includes a namespace prefix (e.g., 'TimeTable:PMR_CONSENT.KEY'), use as-is. Otherwise, the t function will use its default namespace. Default: 'PMR_CONSENT.NO_PREVIOUS_PRACTICE_ADDED' */
    deferredMessageKey?: string
  },
): string | null {
  const deferredKey =
    options?.deferredMessageKey ?? 'PMR_CONSENT.NO_PREVIOUS_PRACTICE_ADDED'

  // When previousMedicalRecord is undefined, show PREVIOUS_PRACTICE_UNKNOWN
  if (!previousMedicalRecord) {
    return t('PMR_CONSENT.PREVIOUS_PRACTICE_UNKNOWN')
  }
  if (priorVetCareStatus === PriorVetCareStatusName.NO) {
    return t('PMR_CONSENT.PATIENT_NOT_BEEN_TO_VET')
  }
  if (
    priorVetCareStatus === PriorVetCareStatusName.UNKNOWN &&
    consentStatus === ConsentStatusName.DEFERRED
  ) {
    // Handle known deferred keys with string literals for parser extraction
    if (deferredKey === 'TimeTable:PMR_CONSENT.CONSENT_NULL_DESCRIPTION') {
      return t('TimeTable:PMR_CONSENT.CONSENT_NULL_DESCRIPTION')
    }
    // Default deferred key
    return t('PMR_CONSENT.NO_PREVIOUS_PRACTICE_ADDED')
  }
  if (priorVetCareStatus === PriorVetCareStatusName.UNKNOWN) {
    return t('PMR_CONSENT.PREVIOUS_PRACTICE_UNKNOWN')
  }
  return null
}
